/**
 * Hit again › Limit and SMS cap — both off by default (user, 20/10/2026:
 * "it should be by default one that always hit … if another drop hits
 * again then hit again. and maybe have an option to set a limit"): No limit
 * | Set a limit (maxTimes null | N hits in a row) and No cap | Set a cap,
 * the number only once you pick the option.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ArrowUpToLine, Repeat2 } from 'lucide-react';
import { DROP_GEN_HIT_LIMITS } from '@trafficpulse/shared';

import { NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DraftHitAgain } from '../../../../../drop-generator.types';
import { ChoiceChips } from '../../../general/controls/ChoiceChips';
import { HIT_FIELD, HIT_UNIT, HitRow } from './HitGrid';
import { ordinal } from './ordinal';

const { amount, maxTimes } = DROP_GEN_HIT_LIMITS;

/** Where a fresh cap starts: twice the container's amount. */
export const startCap = (defaultAmount: number): number => Math.min(amount.max, Math.max(amount.min, defaultAmount * 2));

/** Where a fresh limit starts. */
export const START_LIMIT = 3;

const LIMIT = [
  { value: 'none', label: 'No limit' },
  { value: 'limit', label: 'Set a limit' },
] as const;
const CAP = [
  { value: 'none', label: 'No cap' },
  { value: 'cap', label: 'Set a cap' },
] as const;

interface Props {
  hitAgain: DraftHitAgain;
  defaultAmount: number;
  onPatch: (patch: Partial<DraftHitAgain>) => void;
}

const limitNote = (max: number | null): string => {
  if (max === null) return 'Every drop that hits is sent again — and when that new drop hits too, it is sent again as well, and so on.';
  if (max === 1) return 'A drop that hits is sent again once; that new drop is judged, but not sent again.';
  return `A drop is sent again at most ${max} times in a row; the ${ordinal(max)} new drop is judged, but not sent again.`;
};

export const HitLimitRow: FC<Props> = ({ hitAgain, onPatch }) => {
  const max = hitAgain.maxTimes;
  return (
    <HitRow icon={Repeat2} label="Limit" testId="hit-limit" note={limitNote(max)}>
      <ChoiceChips options={LIMIT} value={max === null ? 'none' : 'limit'} onChange={(v) => onPatch({ maxTimes: v === 'limit' ? (max ?? START_LIMIT) : null })} ariaLabel="A limit on how many times in a row a drop is sent again" />
      {max !== null ? (
        <span className="inline-flex items-center gap-1.5">
          <NumberField value={max} onChange={(v) => onPatch({ maxTimes: v })} min={maxTimes.min} max={maxTimes.max} className={cn(HIT_FIELD, 'w-16')} aria-label="How many times in a row one drop may be sent again" />
          <span className={HIT_UNIT}>{max === 1 ? 'time' : 'times in a row'}</span>
        </span>
      ) : null}
    </HitRow>
  );
};

export const HitCapRow: FC<Props> = ({ hitAgain, defaultAmount, onPatch }) => {
  const on = hitAgain.cap !== null;
  return (
    <HitRow icon={ArrowUpToLine} label="SMS cap" testId="hit-cap" note={on ? 'No hit-again drop sends more than this, whatever the amount says.' : undefined}>
      <ChoiceChips options={CAP} value={on ? 'cap' : 'none'} onChange={(v) => onPatch({ cap: v === 'cap' ? (hitAgain.cap ?? startCap(defaultAmount)) : null })} ariaLabel="A ceiling on each hit-again drop" />
      {on ? (
        <span className="inline-flex items-center gap-1.5">
          <NumberField value={hitAgain.cap ?? startCap(defaultAmount)} onChange={(v) => onPatch({ cap: v })} min={amount.min} max={amount.max} className={cn(HIT_FIELD, 'w-28')} aria-label="Amount cap for hit-again drops" />
          <span className={HIT_UNIT}>SMS per drop</span>
        </span>
      ) : null}
    </HitRow>
  );
};
